import Calendar, { CalendarProps } from 'library/molecules/Calendar'
import styled from '@emotion/styled'
import Modal, {
  ActiveModalProps,
  ModalConfig,
  RecursiveOmitSetActiveModalId,
  MODAL_MIXINS
} from 'shared/library/molecules/Modal'
import useTranslation from 'next-translate/useTranslation'
import ModalContentActiveVoucher, {
  ModalContentActiveVoucherInterface
} from 'library/molecules/ModalContentActiveVoucher'
import ModalHeader from 'library/molecules/ModalHeader'
import ModalContentAddToBasket from 'library/molecules/ModalContentAddToBasket'
import ModalContentError, {
  ModalContentErrorProps
} from 'library/molecules/ModalContentError'
import ModalContentRemoveMembership, {
  ModalContentRemoveMembershipProps
} from 'library/molecules/ModalContentRemoveMembership/ModalContentRemoveMembership'

const CalendarModal = ({
  setActiveModalId,
  availableDays,
  onDateChange,
  initialMonth,
  start
}: ModalProps[MODAL_IDS.calendar] &
  Pick<ActiveModalProps, 'setActiveModalId'>) => {
  const { t } = useTranslation('chargeable')

  return (
    <>
      <ModalHeader
        onClose={() => setActiveModalId(null)}
        headingText={t('select-a-date')}
      />
      <Calendar
        name="calendarSelection"
        showInput={false}
        availableDays={availableDays}
        onDateChange={onDateChange}
        initialMonth={initialMonth}
        start={start}
      />
    </>
  )
}

export enum MODAL_IDS {
  calendar = 'calendar',
  activeVoucher = 'activeVoucher',
  addToBasket = 'addToBasket',
  error = 'error',
  removeMembership = 'removeMembership'
}

export type ModalProps = RecursiveOmitSetActiveModalId<{
  [MODAL_IDS.calendar]: CalendarProps
  [MODAL_IDS.activeVoucher]: ModalContentActiveVoucherInterface
  [MODAL_IDS.addToBasket]: null
  [MODAL_IDS.error]: ModalContentErrorProps
  [MODAL_IDS.removeMembership]?: ModalContentRemoveMembershipProps
}>

export const MODAL_CONFIG: ModalConfig = {
  [MODAL_IDS.calendar]: {
    id: MODAL_IDS.calendar,
    Content: CalendarModal
  },
  [MODAL_IDS.activeVoucher]: {
    id: MODAL_IDS.activeVoucher,
    Content: ModalContentActiveVoucher
  },
  [MODAL_IDS.addToBasket]: {
    id: MODAL_IDS.addToBasket,
    Content: ModalContentAddToBasket
  },
  [MODAL_IDS.error]: {
    id: MODAL_IDS.error,
    Content: ModalContentError
  },
  [MODAL_IDS.removeMembership]: {
    id: MODAL_IDS.removeMembership,
    Content: ModalContentRemoveMembership
  }
}

/**
 * Please refer to https://www.notion.so/eola/Modals-new-widget-d5a5b2c100c840878761cd28bb3279a2
 * for an explanation on our approach to Modals
 */
const ActivityConfigModals = ({
  activeModal,
  ...props
}: Pick<ActiveModalProps, 'activeModal'>) => {
  const { t } = useTranslation()

  const MODAL_TITLE = {
    [MODAL_IDS.addToBasket]: t('chargeable:add-to-basket-success'),
    [MODAL_IDS.error]: t('common:oops'),
    [MODAL_IDS.removeMembership]: t('chargeable:remove-membership')
  }

  const ModalContent = activeModal?.Content ? activeModal?.Content : () => null
  const ModalTitle = activeModal?.id
    ? MODAL_TITLE[activeModal?.id as keyof typeof MODAL_TITLE]
    : undefined

  return (
    <StyledModal
      isOpen={!!activeModal}
      activeModalId={activeModal?.id}
      modalTitle={ModalTitle}
    >
      <ModalContent {...props} />
    </StyledModal>
  )
}

export default ActivityConfigModals

const StyledModal = styled(Modal)(
  ({ theme, activeModalId }) =>
    activeModalId === MODAL_IDS.addToBasket && MODAL_MIXINS.popup(theme),
  ({ theme, activeModalId }) =>
    activeModalId === MODAL_IDS.removeMembership && MODAL_MIXINS.popup(theme),
  ({ theme, activeModalId }) =>
    activeModalId === MODAL_IDS.error && MODAL_MIXINS.popup(theme),
  ({ theme, activeModalId }) =>
    activeModalId === MODAL_IDS.activeVoucher && MODAL_MIXINS.voucher(theme)
)
